import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { TouchableOpacity, StyleSheet, ViewStyle, Animated, View } from 'react-native';

import { useSession } from '../auth';

import { useTheme } from '@/components/theme-provider';
import type { IconName } from '@/types/icons';


export interface ExpandableFABProps {
	onWorkOrderPress?: () => void;
	onRecurringWorkOrderPress?: () => void;
	onInspectionPress?: () => void;
	onQuotePress?: () => void;
	disabled?: boolean;
	style?: ViewStyle;
	size?: 'small' | 'medium' | 'large';
}

interface FABOption {
	icon: IconName;
	label: string;
	onPress?: () => void;
	color?: string;
}

export const ExpandableFAB: React.FC<ExpandableFABProps> = ({
	onWorkOrderPress,
	onRecurringWorkOrderPress,
	onInspectionPress,
	onQuotePress,
	disabled = false,
	style,
	size = 'medium',
}) => {
	const theme = useTheme();
	const { user } = useSession();
	const [isExpanded, setIsExpanded] = useState(false);
	const [animation] = useState(new Animated.Value(0));

	const getSize = () => {
		switch (size) {
			case 'small':
				return { width: 40, height: 40, iconSize: 20 };
			case 'large':
				return { width: 64, height: 64, iconSize: 32 };
			case 'medium':
			default:
				return { width: 56, height: 56, iconSize: 24 };
		}
	};

	const { width, height, iconSize } = getSize();

	const options: FABOption[] = [
		{
			icon: 'document-text-outline',
			label: 'Work Order',
			onPress: onWorkOrderPress,
			color: theme.colors.primary,
		},
	];
	if (onRecurringWorkOrderPress) {
		options.push({
			icon: 'repeat-outline',
			label: 'Recurring WO',
			onPress: onRecurringWorkOrderPress,
			color: theme.colors.primary,
		});
	}
	options.push({
		icon: 'receipt-outline',
		label: 'Quote',
		onPress: onQuotePress,
		color: theme.colors.tertiary,
	});
	// Inspections are only available for "Property Manager" and "Admins"
	/*
	if (user?.role === UserRole.PropertyManager || user?.role === UserRole.Admin) {
		options.push({
			icon: 'camera-outline',
			label: 'Inspection',
			onPress: onInspectionPress,
			color: theme.colors.secondary,
		});
	}
	*/

	const toggleExpanded = () => {
		const toValue = isExpanded ? 0 : 1;
		setIsExpanded(!isExpanded);

		Animated.spring(animation, {
			toValue,
			useNativeDriver: false,
			tension: 100,
			friction: 8,
		}).start();
	};

	const handleOptionPress = (onPress?: () => void) => {
		if (onPress) {
			onPress();
		}
		setIsExpanded(false);
		Animated.spring(animation, {
			toValue: 0,
			useNativeDriver: false,
			tension: 100,
			friction: 8,
		}).start();
	};

	const fabStyle: ViewStyle = {
		width,
		height,
		borderRadius: width / 2,
		backgroundColor: disabled ? theme.colors.outline : theme.colors.primary,
		justifyContent: 'center',
		alignItems: 'center',
		position: 'absolute',
		bottom: 16,
		right: 16,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 4.65,
		elevation: 8,
	};

	const optionButtonStyle: ViewStyle = {
		width: width * 0.8,
		height: height * 0.8,
		borderRadius: (width * 0.8) / 2,
		backgroundColor: theme.colors.surface,
		justifyContent: 'center',
		alignItems: 'center',
		position: 'absolute',
		right: width * 0.4,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.2,
		shadowRadius: 2,
		elevation: 4,
		borderWidth: 1,
		borderColor: theme.colors.outline,
	};

	return (
		<View style={[styles.container, style]}>
			{/* Option buttons */}
			{options.map((option, index) => {
				const translateY = animation.interpolate({
					inputRange: [0, 1],
					outputRange: [0, -(height + 12) * (index + 1) - height],
				});

				const opacity = animation.interpolate({
					inputRange: [0, 0.5, 1],
					outputRange: [0, 0, 1],
				});

				return (
					<Animated.View
						key={option.label}
						style={[
							optionButtonStyle,
							{
								transform: [{ translateY }],
								opacity,
							},
						]}
					>
						<TouchableOpacity
							style={styles.optionButton}
							onPress={() => handleOptionPress(option.onPress)}
							disabled={disabled}
							activeOpacity={0.7}
							accessibilityLabel={option.label}
						>
							<Ionicons
								name={option.icon}
								size={iconSize * 0.8}
								color={option.color || theme.colors.onSurface}
							/>
						</TouchableOpacity>
						<Animated.Text
							style={[
								styles.optionLabel,
								{ opacity },
							]}
						>
							{option.label}
						</Animated.Text>
					</Animated.View>
				);
			})}

			{/* Main FAB button */}
			<TouchableOpacity
				style={[fabStyle, isExpanded && styles.expandedFab]}
				onPress={toggleExpanded}
				disabled={disabled}
				activeOpacity={0.7}
				accessibilityLabel="Create new item"
			>
				<Animated.View
					style={{
						transform: [
							{
								rotate: animation.interpolate({
									inputRange: [0, 1],
									outputRange: ['0deg', '45deg'],
								}),
							},
						],
					}}
				>
					<Ionicons
						name="add"
						size={iconSize}
						color={theme.colors.onPrimary}
					/>
				</Animated.View>
			</TouchableOpacity>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		position: 'absolute',
		bottom: 16,
		right: 16,
	},
	expandedFab: {
		backgroundColor: '#ff6b6b', // Slightly different color when expanded
	},
	optionButton: {
		width: '100%',
		height: '100%',
		justifyContent: 'center',
		alignItems: 'center',
	},
	optionLabel: {
		position: 'absolute',
		right: 50,
		width: 100,
		textAlign: 'right',
		top: '50%',
		marginTop: -10,
		fontSize: 12,
		fontWeight: '500',
		backgroundColor: 'rgba(0, 0, 0, 0.8)',
		color: 'white',
		paddingHorizontal: 8,
		paddingVertical: 4,
		borderRadius: 4,
		overflow: 'hidden',
	},
});
